<template>
  <v-dialog v-model="dialog" max-width="680" persistent>
    <v-card class="movement-dialog" :class="{ dark: isDark }">
      <v-card-title>
        <v-icon class="mr-2" :color="$theme.primary">
          {{ isUnity ? "mdi-source-branch-sync" : "mdi-swap-horizontal-bold" }}
        </v-icon>
        {{ isUnity ? "Enviar para outra unidade" : "Enviar para outra empresa" }}
      </v-card-title>

      <v-card-text>
        <template v-if="isUnity">
          <v-autocomplete
            v-model="unityMove.from_unity"
            :items="unityOptions"
            item-text="nome"
            return-object
            label="Unidade de origem"
            outlined
            dense
            :loading="loadingRefs"
          />
          <v-autocomplete
            v-model="unityMove.to_unity"
            :items="unityOptions"
            item-text="nome"
            return-object
            label="Unidade de destino"
            outlined
            dense
            :loading="loadingRefs"
          />
          <v-select
            v-model="unityMove.movement_type"
            :items="movementTypeOptions"
            item-text="text"
            item-value="value"
            label="Tipo"
            outlined
            dense
          />
          <v-autocomplete
            v-model="unityMove.responsible"
            :items="userOptions"
            item-text="nome"
            return-object
            label="Responsável"
            outlined
            dense
            :loading="loadingRefs"
          />
          <v-textarea v-model="unityMove.note" label="Observação" outlined dense rows="2" />
        </template>

        <template v-else>
          <v-autocomplete
            v-model="companyMove.to_tenant_id"
            :items="tenantOptions"
            :search-input.sync="tenantSearch"
            item-text="nome"
            item-value="_id"
            label="Empresa parceira"
            placeholder="Nome, CNPJ ou telefone"
            outlined
            dense
            :loading="loadingRefs"
            @update:search-input="searchTenants"
          />
          <v-text-field v-model="companyMove.transporter_name" label="Responsável pelo transporte" outlined dense />
          <v-text-field v-model="companyMove.transporter_phone" label="Telefone" outlined dense />
          <v-text-field v-model="companyMove.transporter_vehicle" label="Veículo" outlined dense />
          <v-textarea v-model="companyMove.note" label="Observação" outlined dense rows="2" />
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">Cancelar</v-btn>
        <v-btn :color="$theme.primary" dark :loading="saving" @click="submit">
          Confirmar e imprimir
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";

export default {
  props: {
    sale: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dialog: false,
      mode: "unity",
      loadingRefs: false,
      saving: false,
      tenantSearch: "",
      searchTimer: null,
      unityOptions: [],
      userOptions: [],
      tenantOptions: [],
      unityMove: this.defaultUnityMove(),
      companyMove: this.defaultCompanyMove(),
      movementTypeOptions: [
        { text: "Peça física", value: "physical_piece" },
        { text: "Responsabilidade de montagem", value: "assembly" }
      ]
    };
  },
  computed: {
    isUnity() {
      return this.mode === "unity";
    },
    isDark() {
      return localStorage.dark === "true";
    }
  },
  methods: {
    ...mapActions(["createGlobalMessage"]),
    defaultUnityMove() {
      return {
        from_unity: null,
        to_unity: null,
        movement_type: "physical_piece",
        responsible: null,
        note: ""
      };
    },
    defaultCompanyMove() {
      return {
        to_tenant_id: "",
        transporter_name: "",
        transporter_phone: "",
        transporter_vehicle: "",
        note: ""
      };
    },
    async open(mode) {
      this.mode = mode;
      this.dialog = true;
      this.unityMove = this.defaultUnityMove();
      this.companyMove = this.defaultCompanyMove();
      await this.loadRefs();
    },
    close() {
      if (!this.saving) this.dialog = false;
    },
    async loadRefs() {
      this.loadingRefs = true;
      try {
        if (this.isUnity) {
          const [unities, users] = await Promise.all([
            this.$run("unidades/list", { limitPerPage: 2000 }),
            this.$run("funcionarios/list", { limitPerPage: 2000 })
          ]);
          this.unityOptions = unities.data.docs || [];
          this.userOptions = users.data.docs || [];
          const origin = this.sale.current_unity || this.sale.production_unity || this.sale.unity || null;
          const originId = origin && (origin._id || origin.id || origin);
          this.unityMove.from_unity = this.unityOptions.find(item => String(item._id) === String(originId)) || origin || null;
        } else {
          await this.loadTenants();
        }
      } finally {
        this.loadingRefs = false;
      }
    },
    searchTenants() {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(this.loadTenants, 350);
    },
    async loadTenants() {
      const response = await this.$run("transacoes/search-tenant-partners", {
        search: this.tenantSearch || ""
      });
      this.tenantOptions = response.data.docs || [];
    },
    async submit() {
      this.saving = true;
      try {
        const result = this.isUnity ? await this.submitUnity() : await this.submitCompany();
        this.$emit("saved", result);
        this.dialog = false;
      } catch (error) {
        this.createGlobalMessage({
          type: "error",
          message: error.response?.data?.message || error.message || "Não foi possível registrar a movimentação.",
          timeout: 5000
        });
      } finally {
        this.saving = false;
      }
    },
    async submitUnity() {
      if (!this.unityMove.from_unity || !this.unityMove.to_unity || !this.unityMove.responsible) {
        throw new Error("Informe origem, destino e responsável.");
      }
      const response = await this.$run("transacoes/move-to-unity", {
        _id: this.sale._id,
        ...this.unityMove
      });
      return {
        sale: response.data.sale || response.data,
        movement: response.data.movement,
        type: "unity"
      };
    },
    async submitCompany() {
      if (!this.companyMove.to_tenant_id) {
        throw new Error("Informe a empresa parceira.");
      }
      const response = await this.$run("transacoes/send-to-tenant", {
        _id: this.sale._id,
        ...this.companyMove
      });
      const sale = response.data;
      const movements = (sale.tenant_movements || []).filter(item => item.movement_scope !== "unity");
      return {
        sale,
        movement: movements[movements.length - 1],
        type: "company"
      };
    }
  },
  beforeDestroy() {
    window.clearTimeout(this.searchTimer);
  }
};
</script>

<style scoped>
.movement-dialog {
  border-radius: 8px;
}
.movement-dialog.dark {
  background: #111b21;
  color: #e9edef;
}
.movement-dialog.dark ::v-deep .v-label,
.movement-dialog.dark ::v-deep .v-input input,
.movement-dialog.dark ::v-deep .v-input textarea,
.movement-dialog.dark ::v-deep .v-select__selection {
  color: #dce6ea !important;
}
.movement-dialog.dark ::v-deep fieldset {
  border-color: #3b4d57 !important;
}
</style>
